import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarPlus, Trash, FloppyDisk } from "@phosphor-icons/react";

/** Professional dashboard card: set upcoming availability (date + time slots).
 * Saved via PUT /pro/availability; admins see all entries in the admin panel. */
export const AvailabilityCard = ({ profile, onSaved }) => {
  const [slots, setSlots] = useState(() =>
    (profile.availability || []).map((s) => ({
      ...s,
      times: Array.isArray(s.times) ? s.times : String(s.times || "").split(",").map((t) => t.trim()).filter(Boolean),
    }))
  );
  const [date, setDate] = useState("");
  const [times, setTimes] = useState("");
  const [note, setNote] = useState(profile.availability_note || "");
  const [saving, setSaving] = useState(false);

  const addSlot = () => {
    if (!date) { toast.error("Pick a date first"); return; }
    const list = String(times).split(",").map((t) => t.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one time slot"); return; }
    const bad = list.find((t) => !/^([01]?\d|2[0-3]):[0-5]\d$/.test(t));
    if (bad) { toast.error(`"${bad}" isn't a valid time — use 24h HH:MM, e.g. 14:30`); return; }
    if (slots.some((s) => s.date === date)) { toast.error("That date is already listed — remove it first to change times."); return; }
    setSlots([...slots, { date, times: [...new Set(list)].sort() }].sort((a, b) => a.date.localeCompare(b.date)));
    setDate("");
    setTimes("");
  };

  const removeSlot = (d) => setSlots(slots.filter((s) => s.date !== d));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pro/availability", { slots, note });
      toast.success("Availability updated — the MindDhara team can now see it.");
      onSaved?.(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save availability — check your dates and times");
    } finally { setSaving(false); }
  };

  return (
    <div className="md-card" data-testid="pro-availability-card">
      <div className="flex items-center gap-2 mb-1.5">
        <CalendarPlus size={20} weight="duotone" style={{ color: "var(--md-primary)" }} />
        <h3 className="text-lg font-serif">My upcoming availability</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--md-text-muted)" }}>
        Add the dates and time slots you're open for sessions. The admin team sees these instantly.
      </p>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="md-label-eyebrow block mb-1">Date</label>
          <input type="date" className="md-input" value={date} onChange={(e) => setDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)} data-testid="availability-date-input" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="md-label-eyebrow block mb-1">Times (comma-separated, 24h)</label>
          <input className="md-input" placeholder="10:00, 14:30, 18:00" value={times}
            onChange={(e) => setTimes(e.target.value)} data-testid="availability-times-input" />
        </div>
        <button type="button" onClick={addSlot} className="btn-outline text-sm" data-testid="availability-add-btn">
          <CalendarPlus size={14} weight="bold" /> Add
        </button>
      </div>

      {slots.length === 0 ? (
        <p className="text-sm mb-4" style={{ color: "var(--md-text-soft)" }} data-testid="availability-empty">
          No availability set yet.
        </p>
      ) : (
        <ul className="space-y-2 mb-4" data-testid="availability-list">
          {slots.map((s) => (
            <li key={s.date} className="flex items-center justify-between gap-3 text-sm p-2 rounded-lg"
              style={{ background: "var(--md-bg-alt)" }} data-testid={`availability-slot-${s.date}`}>
              <span>
                <strong>{s.date}</strong>
                <span style={{ color: "var(--md-text-soft)" }}> · {(Array.isArray(s.times) ? s.times : [String(s.times)]).join(", ")}</span>
              </span>
              <button type="button" onClick={() => removeSlot(s.date)} aria-label={`Remove ${s.date}`}
                className="btn-outline text-xs" data-testid={`availability-remove-${s.date}`}>
                <Trash size={12} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mb-4">
        <label className="md-label-eyebrow block mb-1">Note for the team (optional)</label>
        <input className="md-input" placeholder="e.g. Prefer mornings, offline only on Fridays" value={note}
          onChange={(e) => setNote(e.target.value)} data-testid="availability-note-input" />
      </div>

      <button type="button" onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="availability-save-btn">
        <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save availability"}
      </button>
    </div>
  );
};
